/**
 * Date helpers that work on plain `YYYY-MM-DD` strings.
 * All arithmetic is done in UTC so results never depend on the device timezone;
 * "today" is always computed in the farm's timezone (Asia/Jerusalem).
 */

export type IsoDate = string;

export const FARM_TZ = 'Asia/Jerusalem';

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(s: unknown): s is IsoDate {
  if (typeof s !== 'string' || !ISO_RE.test(s)) return false;
  const d = toUtc(s);
  return !Number.isNaN(d.getTime()) && fromUtc(d) === s;
}

function toUtc(iso: IsoDate): Date {
  return new Date(`${iso}T00:00:00Z`);
}

function fromUtc(d: Date): IsoDate {
  return d.toISOString().slice(0, 10);
}

/** Today's date in the farm timezone. */
export function farmToday(now: Date = new Date(), tz: string = FARM_TZ): IsoDate {
  // en-CA formats as YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export function addDays(iso: IsoDate, n: number): IsoDate {
  const d = toUtc(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return fromUtc(d);
}

/** 0 = Sunday … 6 = Saturday (Israeli week starts on Sunday). */
export function dayOfWeek(iso: IsoDate): number {
  return toUtc(iso).getUTCDay();
}

export function startOfWeek(iso: IsoDate): IsoDate {
  return addDays(iso, -dayOfWeek(iso));
}

export function startOfMonth(iso: IsoDate): IsoDate {
  return `${iso.slice(0, 7)}-01`;
}

/** Last day of the month; accepts `YYYY-MM` or `YYYY-MM-DD`. */
export function endOfMonth(isoOrMonth: string): IsoDate {
  const [y, m] = isoOrMonth.split('-').map(Number) as [number, number];
  return fromUtc(new Date(Date.UTC(y, m, 0)));
}

export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((toUtc(to).getTime() - toUtc(from).getTime()) / 86_400_000);
}

export function eachDay(from: IsoDate, to: IsoDate): IsoDate[] {
  const out: IsoDate[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

export interface DateRange {
  from: IsoDate;
  to: IsoDate;
}

/** The range of equal length immediately before `range`. */
export function prevRange({ from, to }: DateRange): DateRange {
  const len = daysBetween(from, to) + 1;
  const prevTo = addDays(from, -1);
  return { from: addDays(prevTo, -(len - 1)), to: prevTo };
}

export type Preset = 'today' | 'week' | 'thismonth' | 'lastmonth' | 'year';

export function presetRange(preset: Preset, today: IsoDate): DateRange {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'week': {
      const from = startOfWeek(today);
      return { from, to: addDays(from, 6) };
    }
    case 'thismonth':
      return { from: startOfMonth(today), to: endOfMonth(today) };
    case 'lastmonth': {
      const last = addDays(startOfMonth(today), -1);
      return { from: startOfMonth(last), to: last };
    }
    case 'year':
      return { from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` };
  }
}

/** `2026-09-06` → `6/9/2026` (legacy display format). */
export function formatDmy(iso: IsoDate | null | undefined): string {
  if (!iso || !isIsoDate(iso)) return '';
  const [y, m, d] = iso.split('-');
  return `${Number(d)}/${Number(m)}/${y}`;
}
